import React, { useEffect, useState } from 'react';
import { AlertTriangle, Square } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import {
  UnendedSession,
  forceEndSession,
  listUnendedSessions,
} from '@/services/fleet';

function staleLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

export const UnendedSessionsCard: React.FC<{ eventId: number | null }> = ({ eventId }) => {
  const [rows, setRows] = useState<UnendedSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRows([]);
    setError(null);
    if (eventId === null) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const refresh = () => {
      void listUnendedSessions(eventId)
        .then((next) => {
          if (!cancelled) {
            setRows(next);
            setError(null);
          }
        })
        .catch(() => {
          if (!cancelled) setError('Could not load open sessions.');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    refresh();
    const interval = window.setInterval(refresh, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [eventId]);

  const end = async (row: UnendedSession) => {
    if (!window.confirm(`End the abandoned recording from ${row.device_id}?`)) return;
    setBusy(row.session_id);
    try {
      await forceEndSession(row.session_id);
      setRows((prev) => prev.filter((r) => r.session_id !== row.session_id));
      setError(null);
    } catch {
      setError('Could not end that session. It may already have ended.');
    } finally {
      setBusy(null);
    }
  };

  if (eventId === null) return null;
  if (loading && rows.length === 0) return null;
  if (!loading && rows.length === 0 && !error) return null;

  return (
    <Card className="mb-4 border-status-warning/40 bg-status-warning/5">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-status-warning" aria-hidden />
          <h3 className="text-sm font-semibold">
            {rows.length} recording{rows.length === 1 ? '' : 's'} never closed
          </h3>
          {loading && <Spinner className="h-3 w-3" />}
        </div>

        <p className="mb-3 max-w-prose text-xs text-muted-foreground">
          These phones stopped uploading but never signalled the end of the recording.
          Until they are closed their footage cannot be finalized for playback.
          Confirm the phone is no longer recording before closing it; uploaded footage is kept.
        </p>

        {error && (
          <div className="mb-3 rounded border border-status-error/40 bg-status-error/10 px-2 py-1 text-xs text-status-error">
            {error}
          </div>
        )}

        <ul className="flex flex-col gap-2">
          {rows.map((r) => (
            <li
              key={r.session_id}
              className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-background px-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{r.device_id}</div>
                <div className="text-xs text-muted-foreground">
                  silent for {staleLabel(r.stale_minutes)} · {r.artifact_count} artifacts received
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={busy === r.session_id}
                onClick={() => void end(r)}
              >
                {busy === r.session_id ? (
                  <Spinner className="h-3 w-3" />
                ) : (
                  <Square className="mr-1 h-3 w-3" aria-hidden />
                )}
                End recording
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default UnendedSessionsCard;
